import {
  CONVERT_EXTENSIONS,
  IMAGES_EXTENSIONS,
  getFileExtension,
} from '@/constants/ingest';
import useDeleteChatbot from '@/hooks/useDeleteChatbot';
import useLimits from '@/hooks/useLimits';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { uploadBucket } from '@/utils/uploadFile';
import { sanitizeName } from '@/utils/utils';

import { useUpgrade } from '@/components/Modals/UpgradeModal';
import useNewNote from '@/hooks/useNewNote';

import { useAuth } from '@/context/AuthContext';
import { useWorkspaceActions } from '@/hooks/useWorkspace';
import * as Sentry from '@sentry/nextjs';
import type { Strategy } from '@unriddle/ai/processPdf';
import { compiler } from 'markdown-to-jsx';
import { renderToString } from 'react-dom/server';
import { toast } from 'sonner';
import { getPdfStrategy } from './pdfStrategy.utils';
import useConvertFile from './useConvertFile';
import { useCreateChatbot } from './useCreateChatbot';
import { useIngestMetadata } from './useIngestMetadata';

const useIngestFile = () => {
  const { exceededLimits, optimisticIncreaseImportLimit } = useLimits();
  const { userPlanDetails } = useAuth();
  const { track } = useAnalytics();
  const { convertFile } = useConvertFile();
  const { showUpgrade } = useUpgrade();

  const { mutateAsync: uploadPdf } = api.file.uploadFile.useMutation();
  const { mutateAsync: uploadUrl } = api.file.uploadUrl.useMutation();
  const { updateChatbot } = useWorkspaceActions();
  const { mutateAsync: updateStatus } = api.ingest.updateStatus.useMutation({
    onMutate: ({ chatbotId, status }) => {
      updateChatbot(chatbotId, {
        ingestionStatus: status,
      });
    },
  });
  const { mutateDelete } = useDeleteChatbot();

  const { createNote } = useNewNote();

  const { createChatbot } = useCreateChatbot();
  const { mutateAsync: updateChatbotUrl } =
    api.file.updateChatbotUrl.useMutation();

  const handleTextOrMarkdownFile = async (file: File) => {
    const reader = new FileReader();

    const text = await new Promise<string>((resolve, reject) => {
      reader.onload = (event) => {
        const result = event.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as text'));
        }
      };
      reader.onerror = () => {
        reject(reader.error);
      };
      reader.readAsText(file);
    });

    if (!text || text.trim() === '') {
      toast.error('The document is empty and contains no text.');
      return null;
    }

    const html = compiler(text);
    const fileName = file.name.replace(/\.(txt|md)$/, '');

    const starterKit = (await import('@/lib/editor/tailwind/extensions'))
      .starterKit;
    const defaultExtensions = (await import('@/lib/editor/tailwind/extensions'))
      .defaultExtensions;
    const generateJSON = (await import('@tiptap/core')).generateJSON;
    const jsonContent = generateJSON(renderToString(html), [
      starterKit(false),
      ...defaultExtensions,
    ]);

    const note = await createNote({
      name: fileName,
      contents: jsonContent,
    });
    if (!note) return;

    return note.id;
  };

  const { ingestGenerateMetadata } = useIngestMetadata();

  const ingestFile = async (file: File, groupId?: string) => {
    if (!userPlanDetails) return null;

    if (exceededLimits?.isDailyPageLimitExceeded) {
      if (userPlanDetails.planName === 'Free') showUpgrade('dailyPageLimit');
      else {
        toast.error('Daily page limit exceeded. Contact support for more.');
      }

      track('dailyPageLimitHit');
      track('Paywall Shown', { reason: 'Daily Page Limit Hit' });

      throw new Error('Daily page limit exceeded');
    }

    if (exceededLimits?.isImportLimitExceeded) {
      showUpgrade('importLimit');
      track('importLimitHit');
      track('Paywall Shown', { reason: 'Import Limit Hit' });

      throw new Error('Import limit exceeded');
    }

    //Limit file size
    if (file.size > userPlanDetails.fileSizeLimit) {
      showUpgrade('sizeLimit');
      track('fileUploadFailed', { reason: 'File too large' });

      throw new Error('File size limit exceeded');
    }

    if (file.type === 'text/plain' || file.type === 'text/markdown') {
      return handleTextOrMarkdownFile(file);
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension === 'md' || fileExtension === 'txt') {
      return handleTextOrMarkdownFile(file);
    }

    const extension = getFileExtension(file.type);
    if (!extension) {
      toast.error(`${file.type} not recognized`);
      return null;
    }

    const requiredConversion = CONVERT_EXTENSIONS.includes(extension);
    if (!requiredConversion && extension !== 'pdf') {
      toast.error(`${extension} files are not supported.`);
      return null;
    }

    const isImage = IMAGES_EXTENSIONS.includes(extension);
    const type = isImage ? 'IMAGE' : 'DOCUMENT';

    const name = sanitizeName(file.name);
    const chatbot = await createChatbot({ name, type, groupId });

    if (!chatbot) return;

    track('chatbotCreated', {
      chatbotId: chatbot.id,
      fileExtension: extension,
      type,
    });

    //Convert file then upload to cloud
    try {
      if (requiredConversion) {
        const url = await convertFile({
          id: chatbot.id,
          name,
          file,
          extension,
        });

        return await uploadUrl({ url, type, name, chatbotId: chatbot.id });
      }
    } catch (error) {
      updateStatus({
        chatbotId: chatbot.id,
        status: 'FAILED',
        failureReason: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }

    //check if pdf is encrypted
    const buffer = await file.arrayBuffer();
    const { parsePdf } = await import('@unriddle/validators/pdf');
    const pdf = await parsePdf(buffer);

    let pageCount = -1;
    try {
      pageCount = pdf.getPageCount();
    } catch (_) {}

    if (pageCount > userPlanDetails.pageLimit) {
      showUpgrade('pageLimit');
      track('pageLimitExceeded');
      track('Paywall Shown', { reason: 'Page Limit Hit' });
      mutateDelete([{ id: chatbot.id }]);

      throw new Error('Page limit exceeded');
    }

    let strategy: Strategy | null = null;
    try {
      strategy = await getPdfStrategy(Buffer.from(buffer));
    } catch (error) {
      Sentry.captureException('PDF is encrypted.', {
        extra: {
          error,
        },
      });
      toast.error(
        'PDF is password protected. Please remove the password and try again.',
      );
      mutateDelete([{ id: chatbot.id }]);
      return null;
    }

    if (strategy !== 'fast' && userPlanDetails.planName === 'Free') {
      showUpgrade('strategyFeature');
      track('Paywall Shown', { reason: 'Strategy feature' });
      mutateDelete([{ id: chatbot.id }]);

      throw new Error('High-res strategy is not available for Free plan');
    }

    optimisticIncreaseImportLimit();

    const { url } = await uploadPdf({
      chatbotId: chatbot.id,
      type,
      version: 'v1',
    });

    await uploadBucket({
      url,
      file,
    });

    await updateChatbotUrl({ chatbotId: chatbot.id, type: 'FILE' });

    ingestGenerateMetadata(chatbot.id, chatbot.name);

    return chatbot.id;
  };
  return { ingestFile };
};

export default useIngestFile;
